import { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/pages/home/components/MovieCard';
import { movies, categories, subcategories } from '@/mocks/movies';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [activeSub, setActiveSub] = useState('全部');

  const category = categories.find((c) => c.slug === slug);
  const subs = subcategories[slug || ''] || ['全部'];

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveSub('全部');
  }, [slug]);

  const filteredMovies = movies.filter((m) => {
    if (m.category !== slug) return false;
    if (activeSub === '全部') return true;
    return m.subcategory === activeSub;
  });

  const categoryName = category?.name || '分类';
  const categoryDesc =
    slug === 'movie'
      ? '最新院线大片、经典电影合集'
      : slug === 'tv'
        ? '热播电视剧全集、高分好剧推荐'
        : slug === 'variety'
          ? '热门综艺节目、爆笑真人秀'
          : slug === 'anime'
            ? '热血国漫、经典日漫'
            : '';

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `极速影视 - ${categoryName}`,
    description: `极速影视${categoryName}频道，${categoryDesc}，每日更新，高清在线观看。`,
    url: `https://www.jisushipin.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '极速影视',
      url: 'https://www.jisushipin.com/',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(webpageSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 md:py-8">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: categoryName },
                ]}
              />
              <div className="flex items-center gap-4 mt-2">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-primary-100 flex items-center justify-center">
                  <i className={`${category?.icon || 'ri-film-line'} text-2xl text-primary-500`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                    {categoryName}
                  </h1>
                  <p className="text-sm text-foreground-500 mt-1">{categoryDesc}</p>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                {subs.map((sub) => (
                  <button
                    key={sub}
                    onClick={() => setActiveSub(sub)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      activeSub === sub
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {sub}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8 md:py-10">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-500">
                共找到 <strong className="text-foreground-900">{filteredMovies.length}</strong> 部作品
              </p>
            </div>
            {filteredMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
                {filteredMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无内容</p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
                >
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}